import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function ModelPrices() {
  return (
    <>
      <p>
        <ScreenLink href="/model-prices">Model prices</ScreenLink> holds the AWS list price of each Bedrock model, in
        dollars per million tokens, with the date each price applies from. Usage is priced from it every day, and it is the
        “AWS list price” cost basis. Billing staff can read it; only super admins can add or delete prices.
      </p>

      <h2>What a price row holds</h2>
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Model</td>
            <td>
              The Bedrock model id, e.g. <code>us.anthropic.claude-sonnet-4-5-20250929-v1:0</code>.
            </td>
          </tr>
          <tr>
            <td>Valid from</td>
            <td>The first UTC day the price applies. It holds until a row with a later date takes over.</td>
          </tr>
          <tr>
            <td>Input, output</td>
            <td>Per million tokens.</td>
          </tr>
          <tr>
            <td>Cache read, cache write</td>
            <td>
              Per million tokens. Cache write is the 5-minute rate. A 1-hour cache write rate is optional; without one,
              1-hour writes are priced at the 5-minute rate and the invoice line says so.
            </td>
          </tr>
          <tr>
            <td>Source, source URL, retrieved on</td>
            <td>Where the price was read, and when.</td>
          </tr>
          <tr>
            <td>Verified</td>
            <td>The price was checked against the AWS Price List. Only verified prices let a month close normally.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Example: Claude Sonnet 4.5 (US cross-region) is $3.30 input and $16.50 output. A day with 2 million input and
        100,000 output tokens costs 2 × $3.30 + 0.1 × $16.50 = $8.25.
      </p>

      <h2 id="adding-a-price">Adding a price</h2>
      <ol>
        <li>
          Choose <strong>Add price</strong>, pick the model and the <strong>valid from</strong> date, and enter the four or
          five rates.
        </li>
        <li>Fill in the source and tick Verified only when you have checked the AWS Price List.</li>
        <li>Save. A second row for the same model and date is refused.</li>
      </ol>
      <p>
        A date in the future takes effect on that day. A date of <strong>today or earlier re-prices past usage</strong>: every
        report for that model and those days changes the next time it is read. Days in a closed month are refused, because a
        closed invoice never changes.
      </p>
      <Callout>
        A model that has usage but no price is never given a default. Its cost shows as unpriced, and a month with such
        usage, or with unverified prices, cannot be closed without a forced close and a reason. See{" "}
        <HelpLink to="closing-a-month" section="forcing" />.
      </Callout>
      <p>A price row that a closed month used can no longer be deleted.</p>

      <Related to={["pricing-and-markup", "closing-a-month", "aws-costs"]} />
    </>
  );
}
